import React from 'react';
import { motion } from 'framer-motion';

// Radiating particle offsets for a quick celebration burst around the pill
const CELEBRATION_BURST_PARTICLES = [
  { dx: -160, dy: -40, icon: '✨', color: '#FDE047', size: 19, delay: 0.05, rot: 35 },
  { dx: -100, dy: -52, icon: '★', color: '#F59E0B', size: 17, delay: 0.1, rot: -25 },
  { dx: 0, dy: -58, icon: '🎉', color: '#38BDF8', size: 22, delay: 0.08, rot: 15 },
  { dx: 100, dy: -52, icon: '✨', color: '#FBBF24', size: 19, delay: 0.12, rot: 45 },
  { dx: 160, dy: -40, icon: '★', color: '#10B981', size: 18, delay: 0.06, rot: -30 },
  { dx: -180, dy: 0, icon: '✦', color: '#FFFFFF', size: 17, delay: 0.15, rot: 20 },
  { dx: 180, dy: 0, icon: '✦', color: '#FCD34D', size: 17, delay: 0.14, rot: -20 },
  { dx: -150, dy: 38, icon: '🎉', color: '#EC4899', size: 19, delay: 0.18, rot: -40 },
  { dx: -65, dy: 46, icon: '✨', color: '#FDE047', size: 18, delay: 0.22, rot: 30 },
  { dx: 65, dy: 46, icon: '★', color: '#F59E0B', size: 17, delay: 0.2, rot: -15 },
  { dx: 150, dy: 38, icon: '✨', color: '#34D399', size: 19, delay: 0.16, rot: 40 },
];

const AMBIENT_CELEBRATION_TWINKLES = [
  { x: -170, y: -26, icon: '⭐', color: '#FBBF24', size: 16, delay: 0.4 },
  { x: 170, y: -26, icon: '⭐', color: '#FDE047', size: 16, delay: 0.55 },
  { x: -75, y: -42, icon: '✨', color: '#38BDF8', size: 18, delay: 0.7 },
  { x: 75, y: -42, icon: '✦', color: '#FFFFFF', size: 15, delay: 0.65 },
  { x: -85, y: 38, icon: '✦', color: '#FBBF24', size: 15, delay: 0.8 },
  { x: 85, y: 38, icon: '✨', color: '#34D399', size: 18, delay: 0.75 },
];

export const PillCelebrationEffect: React.FC = () => {
  return (
    <div
      className="absolute -inset-14 sm:-inset-18 pointer-events-none overflow-visible flex items-center justify-center z-10 select-none bg-transparent"
      aria-hidden="true"
    >
      {/* Expanding celebration shockwave rings around pill */}
      <motion.div
        initial={{ scale: 0.85, opacity: 0.9 }}
        animate={{
          scale: [0.85, 1.28, 1.42],
          opacity: [0.9, 0.4, 0],
        }}
        transition={{
          duration: 1.6,
          repeat: Infinity,
          ease: 'easeOut',
        }}
        className="absolute inset-0 rounded-full border-2 border-amber-400/70 pointer-events-none bg-transparent"
      />
      <motion.div
        initial={{ scale: 0.9, opacity: 0.7 }}
        animate={{
          scale: [0.9, 1.2, 1.34],
          opacity: [0.7, 0.25, 0],
        }}
        transition={{
          duration: 1.6,
          repeat: Infinity,
          delay: 0.45,
          ease: 'easeOut',
        }}
        className="absolute inset-0 rounded-full border border-emerald-400/50 pointer-events-none bg-transparent"
      />

      {/* Radiating celebratory burst particles shooting outward */}
      {CELEBRATION_BURST_PARTICLES.map((p, idx) => (
        <motion.div
          key={`burst-p-${idx}`}
          initial={{ x: 0, y: 0, scale: 0, opacity: 0 }}
          animate={{
            x: [0, p.dx * 1.05, p.dx * 1.25],
            y: [0, p.dy * 1.05, p.dy * 1.25 + 4],
            scale: [0, 1.3, 0.5],
            opacity: [0, 1, 0],
            rotate: [0, p.rot],
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            delay: p.delay,
            ease: 'easeOut',
          }}
          className="absolute text-center leading-none pointer-events-none select-none bg-transparent drop-shadow-[0_2px_8px_rgba(0,0,0,0.9)]"
          style={{
            color: p.color,
            fontSize: `${p.size}px`,
          }}
        >
          {p.icon}
        </motion.div>
      ))}

      {/* Ambient twinkling sparkles */}
      {AMBIENT_CELEBRATION_TWINKLES.map((t, idx) => (
        <motion.div
          key={`twinkle-${idx}`}
          animate={{
            scale: [0.65, 1.2, 0.65],
            opacity: [0.2, 1, 0.2],
            y: [t.y - 2, t.y + 2, t.y - 2],
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            delay: t.delay,
            ease: 'easeInOut',
          }}
          className="absolute pointer-events-none select-none bg-transparent leading-none drop-shadow-[0_2px_8px_rgba(0,0,0,0.9)]"
          style={{
            transform: `translate(${t.x}px, ${t.y}px)`,
            color: t.color,
            fontSize: `${t.size}px`,
          }}
        >
          {t.icon}
        </motion.div>
      ))}
    </div>
  );
};
